import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import {
  EinmalAuftrag,
  EinmalAuftragRecord,
} from '~/common/entities/EinmalAuftraege/EinmalAuftragTypes';
import { IdProvider, unwrap } from '../common/apiCommons';
import axios from 'axios';

export enum EinmalAuftragKeys {
  EINMAL_AUFTRAEGE = 'einmal_auftrag',
  EINMAL_AUFTRAG_RECORDS = 'einmal_auftrag_records',
}

export interface EinmalAuftragCreateRequest {
  name: string;
  description: string;
  duration: number; // seconds
  priority: number;
}

export interface EinmalAuftragEditRequest {
  name: string;
  description: string;
  duration: number; // seconds
  priority: number;
}

const EinmalAuftragQueries = {
  getEinmalAuftraege() {
    return axios.get<EinmalAuftrag[]>('/einmal_auftraege/').then(unwrap);
  },
  getEinmalAuftrag(item: IdProvider) {
    return axios.get<EinmalAuftrag>(`/einmal_auftrag/${item?.id}/`).then(unwrap);
  },
  getEinmalAuftragRecords(filters?: {
    priority?: number;
    einmalAuftragId?: number;
  }) {
    const params: Record<string, number> = {};
    if (filters?.priority !== undefined) params['priority'] = filters.priority;
    if (filters?.einmalAuftragId !== undefined)
      params['einmal_auftrag_id'] = filters.einmalAuftragId;
    return axios
      .get<EinmalAuftragRecord[]>('/einmal_auftrag_records/', { params })
      .then(unwrap);
  },
};

const EinmalAuftragMutations = {
  createEinmalAuftrag(req: EinmalAuftragCreateRequest) {
    return axios.post<EinmalAuftrag>('/einmal_auftrag/', req).then(unwrap);
  },
  editEinmalAuftrag(item: IdProvider, req: EinmalAuftragEditRequest) {
    return axios
      .put<EinmalAuftrag>(`/einmal_auftrag/${item?.id}/`, req)
      .then(unwrap);
  },
  deleteEinmalAuftrag(item: IdProvider) {
    return axios.delete<void>(`/einmal_auftrag/${item?.id}/`).then(unwrap);
  },
  softDeleteEinmalAuftrag(item: IdProvider) {
    return axios
      .post<EinmalAuftrag>(`/einmal_auftrag/${item?.id}/soft_delete/`)
      .then(unwrap);
  },
};

const EinmalAuftragHooks = {
  useEinmalAuftraege(options?: UseQueryOptions<EinmalAuftrag[]>) {
    return useQuery({
      queryKey: [EinmalAuftragKeys.EINMAL_AUFTRAEGE],
      queryFn: EinmalAuftragQueries.getEinmalAuftraege,
      ...options,
    });
  },
  useEinmalAuftrag(item: IdProvider, options?: UseQueryOptions<EinmalAuftrag>) {
    return useQuery({
      queryKey: [EinmalAuftragKeys.EINMAL_AUFTRAEGE, item?.id],
      queryFn: () => EinmalAuftragQueries.getEinmalAuftrag(item),
      ...options,
    });
  },
  useEinmalAuftragRecords(
    filters?: { priority?: number; einmalAuftragId?: number },
    options?: UseQueryOptions<EinmalAuftragRecord[]>,
  ) {
    return useQuery({
      queryKey: [EinmalAuftragKeys.EINMAL_AUFTRAG_RECORDS, filters],
      queryFn: () => EinmalAuftragQueries.getEinmalAuftragRecords(filters),
      ...options,
    });
  },
  useCreateEinmalAuftrag() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: EinmalAuftragMutations.createEinmalAuftrag,
      onSuccess: async () => {
        await queryClient.invalidateQueries([
          EinmalAuftragKeys.EINMAL_AUFTRAEGE,
        ]);
      },
    });
  },
  useUpdateEinmalAuftrag() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: ({ id, ...rest }: IdProvider & EinmalAuftragEditRequest) =>
        EinmalAuftragMutations.editEinmalAuftrag({ id }, rest),
      onSuccess: async (data) => {
        await queryClient.invalidateQueries([
          EinmalAuftragKeys.EINMAL_AUFTRAEGE,
        ]);
        await queryClient.invalidateQueries([
          EinmalAuftragKeys.EINMAL_AUFTRAEGE,
          data.id,
        ]);
      },
    });
  },
  useDeleteEinmalAuftrag() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: EinmalAuftragMutations.deleteEinmalAuftrag,
      onSuccess: async () => {
        await queryClient.invalidateQueries([
          EinmalAuftragKeys.EINMAL_AUFTRAEGE,
        ]);
      },
    });
  },
  useSoftDeleteEinmalAuftrag() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: EinmalAuftragMutations.softDeleteEinmalAuftrag,
      onSuccess: async (data) => {
        await queryClient.invalidateQueries([
          EinmalAuftragKeys.EINMAL_AUFTRAEGE,
        ]);
        await queryClient.invalidateQueries([
          EinmalAuftragKeys.EINMAL_AUFTRAEGE,
          data.id,
        ]);
      },
    });
  },
};

const EinmalAuftragApi = {
  queries: EinmalAuftragQueries,
  mutations: EinmalAuftragMutations,
  hooks: EinmalAuftragHooks,
};

export default EinmalAuftragApi;
